import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PropertyService } from '../../services/property.service';

@Component({
  selector: 'app-create-property-dialog',
  templateUrl: './create-property-dialog.component.html',
  styleUrls: ['./create-property-dialog.component.scss']
})
export class CreatePropertyDialogComponent implements OnInit {
  propertyForm: FormGroup;
  attributes: any[] = [];
  selectedFile: File;
  previewImage: string | ArrayBuffer | null = null;
  originalImageUrl: string | null = null; // Store original image URL when editing
  loading = false;

  constructor(
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private router: Router,
    public dialogRef: MatDialogRef<CreatePropertyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { attributes: any[], property?: any }
  ) {
    this.attributes = (data && data.attributes) ? data.attributes : [];
    const existingProperty = (data && data.property) ? data.property : null;
    
    this.propertyForm = this.fb.group({
      addressLine1: [existingProperty ? (existingProperty.addressLine1 || '') : '', Validators.required],
      addressLine2: [existingProperty ? (existingProperty.addressLine2 || '') : ''],
      city: [existingProperty ? (existingProperty.city || '') : '', Validators.required],
      state: [existingProperty ? (existingProperty.state || '') : '', Validators.required],
      zipCode: [existingProperty ? (existingProperty.zipCode || '') : '', Validators.required],
      monthlyCost: [existingProperty ? (existingProperty.monthlyCost || '') : '', [Validators.required, Validators.min(0)]],
      managed: [existingProperty ? (existingProperty.managed || false) : false],
      coverImage: [existingProperty ? (existingProperty.coverImage || '') : '']
    });

    // Initialize attribute values
    this.attributes.forEach(attr => {
      let value = '';
      if (existingProperty && existingProperty.attributes && Array.isArray(existingProperty.attributes)) {
        const attrValue = existingProperty.attributes.find(a => a.attributeId === attr.id);
        value = attrValue && attrValue.value ? attrValue.value : '';
      }
      this.propertyForm.addControl(`attr_${attr.id}`, this.fb.control(value));
    });

    // Set preview image if editing - store original URL for preservation
    if (existingProperty && existingProperty.coverImage) {
      this.originalImageUrl = existingProperty.coverImage;
      // Set preview - if it's already a full URL, use it; otherwise construct it
      if (existingProperty.coverImage.startsWith('http') || existingProperty.coverImage.startsWith('data:')) {
        this.previewImage = existingProperty.coverImage;
      } else {
        // This will be handled by getImageUrl in the template, but we need a preview
        this.previewImage = existingProperty.coverImage;
      }
    }
  }

  ngOnInit() {
    // Edit is now a standalone page: redirect immediately if opened for edit
    if (this.data && this.data.property && this.data.property.id) {
      this.dialogRef.close();
      this.router.navigate(['edit-property', this.data.property.id]);
      return;
    }
    // If attributes weren't passed, fetch them
    if (this.attributes.length === 0) {
      this.propertyService.getAdminAttributes().then((attrs: any[]) => {
        this.attributes = attrs || [];
        attrs.forEach(attr => {
          const formControlName = `attr_${attr.id}`;
          if (!this.propertyForm.get(formControlName)) {
            let value = '';
            if (this.data && this.data.property && this.data.property.attributes && Array.isArray(this.data.property.attributes)) {
              const attrValue = this.data.property.attributes.find(a => a.attributeId === attr.id);
              value = attrValue && attrValue.value ? attrValue.value : '';
            }
            this.propertyForm.addControl(formControlName, this.fb.control(value));
          } else {
            // Update existing control if property has attributes
            if (this.data && this.data.property && this.data.property.attributes && Array.isArray(this.data.property.attributes)) {
              const attrValue = this.data.property.attributes.find(a => a.attributeId === attr.id);
              if (attrValue && attrValue.value) {
                this.propertyForm.get(formControlName).setValue(attrValue.value);
              }
            }
          }
        });
      });
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const res = e.target && e.target.result;
        this.previewImage = res || null;
        this.propertyForm.patchValue({ coverImage: this.previewImage });
      };
      reader.readAsDataURL(file);
    }
  }

  handleAddressChange(address: any) {
    let street = '';
    let streetNumber = '';
    let line2 = '';

    for (let i = 0; i < address.address_components.length; i++) {
      const component = address.address_components[i];
      if (component.types.includes('administrative_area_level_1')) {
        this.propertyForm.patchValue({ state: component.short_name });
      }
      if (component.types.includes('street_number')) {
        streetNumber = component.short_name;
      }
      if (component.types.includes('route')) {
        street = component.short_name;
      }
      if (component.types.includes('subpremise')) {
        line2 = component.short_name;
      }
      if (component.types.includes('locality')) {
        this.propertyForm.patchValue({ city: component.short_name });
      }
      if (component.types.includes('postal_code')) {
        this.propertyForm.patchValue({ zipCode: component.short_name });
      }
    }

    this.propertyForm.patchValue({
      addressLine1: (streetNumber + ' ' + street).trim(),
      addressLine2: line2 || ''
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSubmit() {
    if (this.propertyForm.valid) {
      this.loading = true;
      const formValue = this.propertyForm.value;
      const existingProperty = (this.data && this.data.property) ? this.data.property : null;
      const isEdit = !!existingProperty;
      
      // Build property object (without base64 image - we'll upload file separately)
      const property: any = {
        addressLine1: formValue.addressLine1,
        addressLine2: formValue.addressLine2 || null,
        city: formValue.city,
        state: formValue.state,
        zipCode: formValue.zipCode,
        monthlyCost: parseFloat(formValue.monthlyCost) || 0,
        managed: formValue.managed || false,
        coverImage: '', // Don't include base64 image in initial creation - too large
        attributes: []
      };

      // If editing and no new file selected, preserve the original image URL
      if (isEdit && !this.selectedFile && this.originalImageUrl) {
        property.coverImage = this.originalImageUrl;
      }

      // Add attribute values - always include all attributes with their values
      this.attributes.forEach(attr => {
        const formControlName = `attr_${attr.id}`;
        const value = formValue[formControlName];
        
        // Get the raw value from the form control to ensure we capture it correctly
        const formControl = this.propertyForm.get(formControlName);
        const rawValue = formControl ? formControl.value : value;
        
        // Convert to string, but preserve the value (don't trim if it's a number like "0")
        const attributeValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
        
        // Always include the attribute with its value
        // The backend checks if value is truthy, so empty strings won't be saved (which is fine)
        property.attributes.push({
          attributeId: attr.id,
          value: attributeValue
        });
      });
      
      // Debug: Log attributes being sent
      console.log('Attributes being sent:', property.attributes);

      // If editing, update existing property
      if (isEdit && existingProperty.id) {
        const propertyToUpdate = { ...property, id: existingProperty.id };
        this.propertyService.updateProperty(propertyToUpdate).then(() => {
          // If we have a new file, upload it
          if (this.selectedFile) {
            this.propertyService.uploadCoverImage(existingProperty.id, this.selectedFile).then(() => {
              this.propertyService.clearProperties();
              this.loading = false;
              this.dialogRef.close({ success: true });
            }).catch((uploadError) => {
              console.error('Error uploading image:', uploadError);
              this.propertyService.clearProperties();
              this.loading = false;
              this.dialogRef.close({ success: true });
            });
          } else {
            this.propertyService.clearProperties();
            this.loading = false;
            this.dialogRef.close({ success: true });
          }
        }).catch(error => {
          console.error('Error updating property:', error);
          this.loading = false;
          this.dialogRef.close({ success: false, error: error });
        });
        return;
      }

      // Create property first (without image)
      this.propertyService.createProperty(property).then((response: any) => {
        // Get the created property ID from the response
        let createdPropertyId = null;
        
        // Try to get ID from response
        if (response && response.id) {
          createdPropertyId = response.id;
        } else if (response && response.data && response.data.id) {
          createdPropertyId = response.data.id;
        }

        // If we have a property ID and a file, upload the image
        if (createdPropertyId && this.selectedFile) {
          this.propertyService.uploadCoverImage(createdPropertyId, this.selectedFile).then(() => {
            // Refresh properties to get the updated property with image
            this.propertyService.clearProperties();
            this.propertyService.getProperties().then(properties => {
              const createdProperty = properties.find(p => p.id === createdPropertyId);
              this.loading = false;
              this.dialogRef.close({ success: true, property: createdProperty });
            }).catch(() => {
              this.loading = false;
              this.dialogRef.close({ success: true });
            });
          }).catch((uploadError) => {
            console.error('Error uploading image:', uploadError);
            // Property was created, just image upload failed
            this.propertyService.clearProperties();
            this.propertyService.getProperties().then(properties => {
              const createdProperty = properties.find(p => p.id === createdPropertyId);
              this.loading = false;
              this.dialogRef.close({ success: true, property: createdProperty });
            }).catch(() => {
              this.loading = false;
              this.dialogRef.close({ success: true });
            });
          });
        } else {
          // No image to upload, just refresh properties
          this.propertyService.clearProperties();
          this.propertyService.getProperties().then(properties => {
            const createdProperty = createdPropertyId 
              ? properties.find(p => p.id === createdPropertyId)
              : properties.find(p => 
                  p.addressLine1 === property.addressLine1 &&
                  p.city === property.city
                );
            this.loading = false;
            this.dialogRef.close({ success: true, property: createdProperty });
          }).catch(() => {
            this.loading = false;
            this.dialogRef.close({ success: true });
          });
        }
      }).catch(error => {
        console.error('Error creating property:', error);
        this.loading = false;
        // Still close dialog but indicate error
        this.dialogRef.close({ success: false, error: error });
      });
    }
  }

  // Map an attribute's declared type to an HTML input type.
  inputTypeFor(attr: any): string {
    const type = (attr && attr.attributeType ? String(attr.attributeType) : '').toLowerCase();
    if (type === 'number') {
      return 'number';
    }
    if (type === 'date') {
      return 'date';
    }
    return 'text';
  }
}
